<template>
  <!--
    Crop a product logo to the 2:1 slot it will be displayed in.

    The Products list draws every logo in a fixed 100x50 box with
    `object-fit: contain` (.kf-logo, app-css/kf-tokens.css). Anything narrower than
    2:1 hits the height cap first and gets centred, leaving background showing down
    both sides -- a square logo renders 42x42 in a 100px box, so 29px of padding
    each side. Cropping in a LOCKED 2:1 frame is what makes that impossible rather
    than merely unlikely.

    Hand-rolled rather than a dependency: the project carries no image library and
    no canvas code at all, and this needs pan, zoom and one toBlob.
  -->
  <v-dialog :value="value" max-width="560" persistent @input="$emit('input', $event)">
    <v-card class="kf-crop">
      <v-card-title class="kf-crop__title">
        <LucideIcon name="crop" :size="22" class="mr-2" />
        Crop logo
      </v-card-title>

      <v-card-text class="kf-crop__body">
        <!-- The frame IS the crop. Fixed 2:1 at 4x the output's 100x50 so the
             admin can see what they are doing; the image moves underneath it. -->
        <div
          ref="frame"
          class="kf-crop__frame"
          @mousedown="startDrag"
          @touchstart.prevent="startDrag"
        >
          <img
            v-if="src"
            ref="img"
            :src="src"
            class="kf-crop__img"
            :style="imgStyle"
            crossorigin="anonymous"
            alt=""
            draggable="false"
            @load="onImageLoad"
            @error="onImageError"
          />
          <div class="kf-crop__grid"></div>
        </div>

        <div v-if="error" class="kf-crop__error">{{ error }}</div>

        <div class="kf-crop__controls">
          <LucideIcon name="zoom-out" :size="18" class="mr-2" />
          <v-slider
            v-model="zoom"
            :min="1"
            :max="4"
            :step="0.01"
            hide-details
            dense
            class="kf-crop__slider"
            @input="clampOffset"
          />
          <LucideIcon name="zoom-in" :size="18" class="ml-2" />
        </div>

        <div class="kf-crop__hint">
          Drag to reposition, slide to zoom. Saved at {{ OUT_W }}&times;{{ OUT_H }} —
          the shape the logo is displayed in, so it will not sit on a padded background.
        </div>
      </v-card-text>

      <v-card-actions class="kf-crop__actions">
        <v-btn text small class="text-none" :disabled="working" @click="$emit('cancel')">
          Cancel
        </v-btn>
        <v-spacer />
        <v-btn text small class="text-none" :disabled="working || !loaded" @click="reset">
          Reset
        </v-btn>
        <v-btn
          color="primary"
          depressed
          small
          class="text-none"
          :loading="working"
          :disabled="!loaded"
          @click="apply"
        >
          Use this crop
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import LucideIcon from "@/components/ui/LucideIcon.vue";

// The frame the admin drags in. 2:1, matching .kf-logo, at a workable size.
const FRAME_W = 400;
const FRAME_H = 200;

export default {
  name: "KfLogoCropDialog",
  components: { LucideIcon },
  props: {
    value: { type: Boolean, default: false },
    /** Object URL for a freshly picked file, or the stored logo's URL. */
    src: { type: String, default: null },
  },
  data() {
    return {
      // 200x100 -- the canonical size seed_karrofin_products.py already writes.
      OUT_W: 200,
      OUT_H: 100,
      FRAME_W,
      FRAME_H,
      zoom: 1,
      offsetX: 0,
      offsetY: 0,
      baseW: 0,
      baseH: 0,
      loaded: false,
      working: false,
      error: null,
      drag: null,
    };
  },
  computed: {
    /** Rendered size at the current zoom. */
    drawW() {
      return this.baseW * this.zoom;
    },
    drawH() {
      return this.baseH * this.zoom;
    },
    imgStyle() {
      return {
        width: this.drawW + "px",
        height: this.drawH + "px",
        transform:
          `translate(-50%, -50%) translate(${this.offsetX}px, ${this.offsetY}px)`,
      };
    },
  },
  watch: {
    src() {
      this.loaded = false;
      this.error = null;
    },
    value(open) {
      if (open) {
        this.zoom = 1;
        this.offsetX = 0;
        this.offsetY = 0;
        this.error = null;
      } else {
        this.endDrag();
      }
    },
  },
  beforeDestroy() {
    this.endDrag();
  },
  methods: {
    /**
     * Seed the image at "cover" scale, not "contain".
     *
     * Contain would start with the very letterboxing this dialog exists to remove,
     * and the admin would have to zoom in before the crop was any use. Cover fills
     * the frame, so the default crop is already padding-free.
     */
    onImageLoad(e) {
      const img = e.target;
      const nw = img.naturalWidth || 1;
      const nh = img.naturalHeight || 1;
      const scale = Math.max(FRAME_W / nw, FRAME_H / nh);
      this.baseW = nw * scale;
      this.baseH = nh * scale;
      this.zoom = 1;
      this.offsetX = 0;
      this.offsetY = 0;
      this.loaded = true;
      this.error = null;
    },
    onImageError() {
      this.loaded = false;
      this.error = "That image could not be loaded.";
    },
    reset() {
      this.zoom = 1;
      this.offsetX = 0;
      this.offsetY = 0;
    },
    startDrag(e) {
      if (!this.loaded) return;
      const point = e.touches ? e.touches[0] : e;
      this.drag = {
        x: point.clientX,
        y: point.clientY,
        ox: this.offsetX,
        oy: this.offsetY,
      };
      window.addEventListener("mousemove", this.onDrag);
      window.addEventListener("mouseup", this.endDrag);
      window.addEventListener("touchmove", this.onDrag, { passive: false });
      window.addEventListener("touchend", this.endDrag);
    },
    onDrag(e) {
      if (!this.drag) return;
      if (e.cancelable) e.preventDefault();
      const point = e.touches ? e.touches[0] : e;
      this.offsetX = this.drag.ox + (point.clientX - this.drag.x);
      this.offsetY = this.drag.oy + (point.clientY - this.drag.y);
      this.clampOffset();
    },
    endDrag() {
      this.drag = null;
      window.removeEventListener("mousemove", this.onDrag);
      window.removeEventListener("mouseup", this.endDrag);
      window.removeEventListener("touchmove", this.onDrag);
      window.removeEventListener("touchend", this.endDrag);
    },
    /**
     * Never let the frame see past the edge of the image.
     *
     * Without this the admin can drag the logo half out of the frame and save a
     * crop with a transparent or white strip down one side -- the same padding
     * problem, just self-inflicted.
     */
    clampOffset() {
      const maxX = Math.max(0, (this.drawW - FRAME_W) / 2);
      const maxY = Math.max(0, (this.drawH - FRAME_H) / 2);
      this.offsetX = Math.min(maxX, Math.max(-maxX, this.offsetX));
      this.offsetY = Math.min(maxY, Math.max(-maxY, this.offsetY));
    },
    /**
     * Draw the framed region at output size and hand back a PNG blob.
     *
     * PNG, not JPEG: these logos are routinely transparent and JPEG has no alpha,
     * so a wordmark would come back sitting on a black rectangle.
     */
    apply() {
      const img = this.$refs.img;
      if (!img || !this.loaded) return;
      this.working = true;
      this.error = null;

      try {
        // Frame coordinates -> source pixels. The image is centred in the frame and
        // then translated, so the frame's top-left in rendered space is:
        const renderedLeft = (FRAME_W - this.drawW) / 2 + this.offsetX;
        const renderedTop = (FRAME_H - this.drawH) / 2 + this.offsetY;
        const ratio = (img.naturalWidth || 1) / this.drawW;

        const sx = (0 - renderedLeft) * ratio;
        const sy = (0 - renderedTop) * ratio;
        const sw = FRAME_W * ratio;
        const sh = FRAME_H * ratio;

        const canvas = document.createElement("canvas");
        canvas.width = this.OUT_W;
        canvas.height = this.OUT_H;
        const ctx = canvas.getContext("2d");
        ctx.imageSmoothingQuality = "high";
        ctx.drawImage(img, sx, sy, sw, sh, 0, 0, this.OUT_W, this.OUT_H);

        canvas.toBlob((blob) => {
          this.working = false;
          if (!blob) {
            this.error = "Could not process that image.";
            return;
          }
          this.$emit("crop", blob);
        }, "image/png");
      } catch (err) {
        // A tainted canvas throws here. It should not: media is served with
        // Access-Control-Allow-Origin and the <img> carries crossorigin, but say
        // so plainly rather than failing silently if that ever regresses.
        this.working = false;
        this.error =
          "Could not read that image. If it is an existing logo, the server may no " +
          "longer be sending CORS headers for /media/.";
      }
    },
  },
};
</script>

<style scoped>
.kf-crop__title {
  font-size: 16px;
  font-weight: 700;
  color: var(--kf-brand);
  display: flex;
  align-items: center;
}
.kf-crop__body {
  padding-top: 8px;
}
/* The frame is the crop region, so it is exactly 2:1 and its overflow is what
   gets discarded. The checkerboard shows transparency honestly -- a white
   background here would hide that a logo has no backdrop of its own. */
.kf-crop__frame {
  position: relative;
  width: 400px;
  height: 200px;
  max-width: 100%;
  margin: 0 auto;
  overflow: hidden;
  border-radius: var(--kf-radius);
  border: 1px solid var(--kf-border);
  cursor: grab;
  touch-action: none;
  background-color: #ffffff;
  background-image: linear-gradient(45deg, #eceff3 25%, transparent 25%),
    linear-gradient(-45deg, #eceff3 25%, transparent 25%),
    linear-gradient(45deg, transparent 75%, #eceff3 75%),
    linear-gradient(-45deg, transparent 75%, #eceff3 75%);
  background-size: 16px 16px;
  background-position: 0 0, 0 8px, 8px -8px, -8px 0;
}
.kf-crop__frame:active {
  cursor: grabbing;
}
/* Centred on the frame, then panned. Both halves live in ONE transform: a
   percentage margin would resolve against the frame's width rather than the
   image's, and mixing the standalone `translate` property with `transform`
   relies on composition order. The single transform is what apply()'s
   arithmetic assumes -- rendered top-left = (FRAME - drawn) / 2 + offset. */
.kf-crop__img {
  position: absolute;
  left: 50%;
  top: 50%;
  user-select: none;
  -webkit-user-drag: none;
}
.kf-crop__grid {
  position: absolute;
  inset: 0;
  pointer-events: none;
  background-image: linear-gradient(to right, rgba(255, 255, 255, 0.5) 1px, transparent 1px),
    linear-gradient(to bottom, rgba(255, 255, 255, 0.5) 1px, transparent 1px);
  background-size: 33.33% 33.33%;
}
.kf-crop__controls {
  display: flex;
  align-items: center;
  margin-top: 14px;
  color: var(--kf-text-muted);
}
.kf-crop__slider {
  margin-top: 0;
}
.kf-crop__hint {
  font-size: var(--kf-fs-label);
  color: var(--kf-text-muted);
  margin-top: 6px;
}
.kf-crop__error {
  font-size: var(--kf-fs-label);
  color: #c62828;
  margin-top: 8px;
}
.kf-crop__actions {
  padding: 8px 16px 14px;
}
</style>
